/**
 * Dimension Rift — Plan 4a Task 10.1
 *
 * Swaps positions between caster and a target player. The caster rolls afterward.
 * Hostile non-self swaps are blocked by Stone Skin.
 *
 * Rules:
 * - `targetType: 'player'`, `blockedByStoneSkin: true`.
 * - Missing targetPlayerId → throws Error.
 * - Swaps caster.nodeId ↔ target.nodeId, emits `item_cast` + `item_swap_positions`,
 *   return {success:true, endsTurn:false}.
 * - Self-target is a no-op (same node → same node) but events still fire.
 *
 * Self-registers via addItem(dimensionRift) at module load.
 */
import type { GameState } from '../types';
import type { ItemCastCtx, ItemCastResult, Item } from './types';
import { appendEvent } from '../event-log';
import { addItem } from './registry';
import { clearLastMove } from '../movement-options';
import { tryBlockWithStoneSkin } from '../stone-skin-block';

export const dimensionRift: Item = {
  id: 'dimension_rift',
  name: 'Dimension Rift',
  description: 'Swap positions with any player, then roll.',
  targetType: 'player',
  category: 'simple',
  rarity: 'rare',
  blockedByStoneSkin: true,

  cast(state: GameState, ctx: ItemCastCtx): ItemCastResult {
    const { casterId } = ctx;
    const targetId = ctx.target.targetPlayerId;

    // Step 1: Validate target
    if (!targetId) throw new Error('Dimension Rift: missing targetPlayerId in ctx.target');

    const caster = state.players.get(casterId);
    if (!caster) throw new Error(`Dimension Rift: caster not found: ${casterId}`);

    const target = state.players.get(targetId);
    if (!target) throw new Error(`Dimension Rift: target not found: ${targetId}`);

    // Step 2: Emit item_cast
    appendEvent(state, {
      type: 'item_cast',
      playerId: casterId,
      casterId,
      itemId: 'dimension_rift',
      targetId,
    });

    if (
      tryBlockWithStoneSkin(state, targetId, {
        actorId: casterId,
        sourceId: 'dimension_rift',
      })
    ) {
      return { success: true, endsTurn: false, blockedByStoneSkin: true };
    }

    // Step 3: Swap positions
    const casterOldNodeId = caster.nodeId;
    const targetOldNodeId = target.nodeId;

    caster.nodeId = targetOldNodeId;
    target.nodeId = casterOldNodeId;
    clearLastMove(state, casterId);
    clearLastMove(state, targetId);

    appendEvent(state, {
      type: 'item_swap_positions',
      playerId: casterId,
      casterId,
      targetId,
      casterOldNodeId,
      targetOldNodeId,
    });

    return {
      success: true,
      endsTurn: false,
      forcedRelocations: [
        {
          playerId: casterId,
          actorId: casterId,
          fromNodeId: casterOldNodeId,
          toNodeId: targetOldNodeId,
          reason: 'swap',
          source: 'dimension_rift',
        },
        {
          playerId: targetId,
          actorId: casterId,
          fromNodeId: targetOldNodeId,
          toNodeId: casterOldNodeId,
          reason: 'swap',
          source: 'dimension_rift',
        },
      ],
    };
  },
};

// Self-register at module load
addItem(dimensionRift);
